import { updateState, withDevtools } from '@angular-architects/ngrx-toolkit';
import { computed, effect, inject } from '@angular/core';
import { signalStore, withComputed, withHooks, withMethods, withState } from '@ngrx/signals';
import { tap } from 'rxjs/operators';

import { MeasurementSystem } from '@models/module/fields/enums/measurementSystem';
import { Tenant } from '@models/module/tenant';
import { TenantSetting, TenantSettingsEnum, TenantSettingValue } from '@models/tenant';
import {
  DateFormats,
  DateFormatType,
  DecimalSeparator,
  DecimalSeparatorType,
  ThousandSeparator,
  ThousandSeparatorType,
  TimeFormats,
  TimeFormatType,
} from '@models/user';
import { TenantSettingsService } from '@module-instance/tenant-settings/common';
import { TENANT } from '@shared/consts';
import { TenantService } from '@shared/services';
import { EnvironmentConfigService } from '@shared/services/environment-config.service';
import { environment } from 'src/environments/environment';
import { DictionaryStore } from './dictionary.store';
import { initialTenantState as initialState, TenantStoreActions } from './models';

export const TenantStore = signalStore(
  { providedIn: 'root' },
  withState(initialState),
  withComputed(store => {
    const configService = inject(EnvironmentConfigService);
    return {
      selectedTenantName: computed(() => store.selectedTenant()?.name.toLowerCase()),
      isAiEnabled: computed(() => configService.isAiEnabled()),
    };
  }),
  withDevtools('tenant'),
  withMethods(store => {
    const tenantSettingsService = inject(TenantSettingsService);
    const tenantService = inject(TenantService);
    const dictionaryStore = inject(DictionaryStore);
    const configService = inject(EnvironmentConfigService);
    const selectTenant = (tenant?: Tenant) => {
      if (tenant) {
        sessionStorage.setItem(TENANT, tenant.id.toString());
      } else {
        sessionStorage.removeItem(TENANT);
        dictionaryStore.clearState();
      }
      updateState(store, TenantStoreActions.SelectTenant, { selectedTenant: tenant });
    };

    const getCalculatedValues = (settings: TenantSetting[]): TenantSetting[] => {
      return [...(settings || [])].map(setting => ({ ...setting, calculatedValue: getCalculatedValue(setting) }));
    };

    const getCalculatedValue = (setting: TenantSetting): TenantSettingValue | undefined => {
      if (setting.type === 'boolean') {
        return getBool(setting.value);
      }
      switch (setting.id) {
        case TenantSettingsEnum.date_format:
          return getValueFromMap(DateFormats, DateFormatType as any, setting.value);
        case TenantSettingsEnum.time_format:
          return getValueFromMap(TimeFormats, TimeFormatType as any, setting.value);
        case TenantSettingsEnum.decimal_separator:
          return getValueFromMap(DecimalSeparator, DecimalSeparatorType as any, setting.value);
        case TenantSettingsEnum.thousand_separator:
          return getValueFromMap(ThousandSeparator, ThousandSeparatorType as any, setting.value);
        case TenantSettingsEnum.load_more:
        case TenantSettingsEnum.show_last_visited_module:
          return getBool(setting.value);
        case TenantSettingsEnum.measurement_system:
          return getValueFromEnum(MeasurementSystem, setting.value);
      }
      return setting.value;
    };

    const getValueFromMap = <T, V>(map: Map<T, V>, enumValue: T, value: TenantSettingValue | undefined | null): V => {
      if (!Number.isNaN(Number(value))) {
        return map.get(Number(value) as any);
      }
      return map.get(enumValue[value.toString()]);
    };

    const getValueFromEnum = <T>(enumValue: T, value: TenantSettingValue | undefined | null): keyof T => {
      if (!Number.isNaN(Number(value))) {
        return enumValue[Number(value)] as keyof T;
      }
      return value as keyof T;
    };

    const getBool = (value: TenantSettingValue | undefined | null): boolean => {
      return ['true', 'yes', '1'].includes(value?.toString()?.toLowerCase());
    };

    return {
      loadSettings: () => {
        updateState(store, TenantStoreActions.LoadingSettings, { loadingSettings: true });
        tenantSettingsService.getTenantSettings().subscribe(settings =>
          updateState(store, TenantStoreActions.SetSettings, {
            settings: getCalculatedValues(settings),
            loadingSettings: false,
          }),
        );
      },
      loadTenants: () => {
        const currentSelectedTenantId = store.selectedTenant()?.id;
        updateState(store, TenantStoreActions.LoadingTenants, { isLoading: true });
        return tenantService.getTenants().pipe(
          tap(availableTenants =>
            updateState(store, TenantStoreActions.SetTenants, {
              availableTenants,
              selectedTenant: (availableTenants ?? []).find(a => a.id == currentSelectedTenantId),
              isLoading: false,
            }),
          ),
        );
      },
      getIdByName: (name: string) => {
        const tenant = store.availableTenants().find(t => t.name.toLowerCase() === name.toLowerCase());
        return tenant ? tenant.id : null;
      },
      selectTenant,
      // This is user in tests
      updateSettings: (settings: TenantSetting[]) => {
        updateState(store, TenantStoreActions.LoadingSettings, { loadingSettings: true });
        setTimeout(() => {
          updateState(store, TenantStoreActions.SetSettings, {
            settings: getCalculatedValues(settings),
            loadingSettings: false,
          });
        }, 0);
      },
      updateSetting: (id: number, value: TenantSettingValue) => {
        const settings = store.settings().map(s => (s.id === id ? { ...s, value } : s));
        updateState(store, TenantStoreActions.UpdateSetting, { settings: getCalculatedValues(settings) });
      },
      getSettings: (ids: TenantSettingsEnum[]) => store.settings().filter(s => ids.includes(s.id)),
      getSetting: (id: TenantSettingsEnum) => store.settings().find(s => id === s.id),
      getSettingValue: (id: TenantSettingsEnum) => store.settings().find(s => id === s.id)?.value,

      // Enable demo space for all tenants in development or demo tenants in production
      isDemoTenant: () => !environment.production || environment.isDemoTenant,

      updateAiEnabled: (enabled: boolean) => configService.updateAiEnabled(enabled),
    };
  }),
  withHooks((store, configService = inject(EnvironmentConfigService)) => ({
    onInit: () => {
      effect(() => {
        if (store.selectedTenant()?.id) {
          store.loadSettings();
          configService.loadConfig();
        }
      });
    },
  })),
);
